import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Progress } from '../ui/progress';
import { Upload, FileImage, X, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { useTranslation } from '../../i18n';

export function LogoUploader({
  onLogoUploaded, currentLogoUrl }) {
  const { t } = useTranslation();
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);

    const onDrop = useCallback(async (acceptedFiles) => {
        if (acceptedFiles.length === 0) return;

        const selectedFile = acceptedFiles[0];
        setFile({
            file: selectedFile,
            name: selectedFile.name,
            size: selectedFile.size,
            preview: URL.createObjectURL(selectedFile)
        });

        // Auto-upload immediately upon drop
        await handleUpload(selectedFile);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.webp']
        },
        maxFiles: 1,
        multiple: false
    });

    const handleUpload = async (uploadFile) => {
        setUploading(true);
        setProgress(20);

        try {
            // 1. Get presigned URL
            const { presignedUrl, fileKey } = await api.post('/organizations/logo/presigned-url', {
                fileName: uploadFile.name,
                contentType: uploadFile.type || 'image/png'
            });

            setProgress(60);

            // 2. PUT directly to S3
            const putResponse = await fetch(presignedUrl, {
                method: 'PUT',
                body: uploadFile,
                headers: {
                    'Content-Type': uploadFile.type || 'image/png',
                    'x-amz-server-side-encryption': 'AES256',
                },
            });

            if (!putResponse.ok) {
                throw new Error('Failed to upload file to storage');
            }

            setProgress(100);
            toast.success('Logo uploaded successfully');

            // 3. Callback with the S3 file locator
            if (onLogoUploaded) {
                onLogoUploaded(fileKey);
            }

        } catch (error) {
            console.error(error);
            toast.error('Failed to upload logo');
            setFile(null); // Reset on failure
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    const clearLogo = (e) => {
        e.stopPropagation();
        setFile(null);
        if (onLogoUploaded) onLogoUploaded(''); // Signal clearing
    };

    return (
        <div className="space-y-4">
            <div
                {...getRootProps()}
                className={`relative flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 transition-all cursor-pointer hover:bg-muted/50 ${isDragActive ? 'border-primary bg-primary/5' : 'border-border'
                    }`}
            >
                <input {...getInputProps()} />

                {uploading ? (
                    <div className="flex flex-col items-center py-4">
                        <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
                        <p className="text-sm font-medium">Uploading logo...</p>
                        <Progress value={progress} className="w-full mt-4 h-2 max-w-xs" />
                    </div>
                ) : file?.preview || currentLogoUrl ? (
                    <div className="relative group">
                        <div className="h-24 w-auto flex items-center justify-center bg-card rounded shadow-sm border p-4">
                            <img
                                src={file?.preview || currentLogoUrl}
                                alt="Logo Preview"
                                className="max-h-16 w-auto object-contain"
                            />
                        </div>

                        <button
                            onClick={clearLogo}
                            className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shadow-sm"
                            title="Remove logo"
                        >
                            <X className="h-4 w-4" />
                        </button>
                        <p className="text-xs text-center mt-2 text-muted-foreground group-hover:text-primary transition-colors">
                            Click or drag to replace
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col items-center py-4">
                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                            <Upload className="h-6 w-6 text-primary" />
                        </div>
                        <p className="text-sm font-medium">Click to upload logo</p>
                        <p className="text-xs text-muted-foreground mt-1">SVG, PNG, or JPG (max 2MB)</p>
                    </div>
                )}
            </div>
        </div>
    );
}
